// src/lib/localPersistence.ts
/**
 * Local Persistence (T-23)
 * 
 * Automatic draft saving and restoration using localStorage.
 * Builds on T-20 serialization infrastructure with size optimization.
 * 
 * Acceptance Criteria: "Reload restores draft"
 */

import { serialize, hydrate, type SceneExport, type SerializationOptions } from './serialization';
import type { RootState } from '../store';

// =============================================================================
// CONSTANTS & CONFIGURATION
// =============================================================================

/**
 * localStorage key for draft storage
 */
export const DRAFT_STORAGE_KEY = 'nl:draft';

/**
 * Debounce interval for auto-save operations (2 seconds)
 */
export const SAVE_DEBOUNCE_MS = 2000;

/**
 * Maximum storage size limit (90KB, well under 100KB localStorage limit)
 */
export const MAX_STORAGE_SIZE = 90000;

/**
 * Size threshold for excluding annotations (70KB)
 */
export const ANNOTATION_EXCLUSION_THRESHOLD = 70000;

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * Draft metadata for UI display and management
 */
export interface DraftMetadata {
  timestamp: string;
  size: number;
  hasAnnotations: boolean;
  stateSlices: string[];
}

/**
 * Result of draft save operation
 */
export interface SaveDraftResult {
  success: boolean;
  size?: number;
  excludedAnnotations?: boolean;
  error?: string;
}

/**
 * Result of draft load operation
 */
export interface LoadDraftResult {
  success: boolean;
  data?: any;
  metadata?: DraftMetadata;
  error?: string;
}

// =============================================================================
// CORE PERSISTENCE FUNCTIONS
// =============================================================================

/**
 * Safe JSON.stringify that handles circular references
 */
function safeStringify(obj: any, timeout: number = 5000): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      console.error('⏰ JSON.stringify timeout - circular reference detected');
      reject(new Error('JSON.stringify timeout - circular reference detected'));
    }, timeout);
    
    try {
      // Detect and remove circular references
      const seen = new WeakSet();
      const result = JSON.stringify(obj, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            console.warn(`🔄 Circular reference detected at key: ${key}`);
            return '[Circular Reference Removed]';
          }
          seen.add(value);
        }
        return value;
      });
      
      clearTimeout(timeoutId);
      console.log('✅ Safe JSON.stringify completed successfully');
      resolve(result);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('❌ JSON.stringify failed:', error);
      reject(error);
    }
  });
}

/**
 * FIXED: Save current Redux state as draft to localStorage
 * Now handles circular references properly
 */
export async function saveDraftToStorage(state: RootState): Promise<SaveDraftResult> {
  console.log('💾 saveDraftToStorage called');
  
  if (!state.preferences.autoSave) {
    console.log('❌ Auto-save disabled in preferences');
    return { success: false, error: 'Auto-save disabled in preferences' };
  }

  try {
    console.log('📦 Serializing state...');
    console.log('🔍 State keys:', Object.keys(state));
    console.log('🔍 Secondary line state:', state.secondaryLine);
    
    let options: SerializationOptions = {
      includeTimestamp: true,
      excludePreferences: false,
      excludeAnnotations: false,
    };

    console.log('🔍 Calling serialize function...');
    let exported = serialize(state, options);
    console.log('✅ Serialize completed, starting safe JSON.stringify...');
    console.log('🔍 Exported keys:', Object.keys(exported));
    
    let jsonData: string;
    try {
      console.log('🔍 Starting safe JSON.stringify...');
      // CRITICAL FIX: Use safe stringify instead of regular JSON.stringify
      jsonData = await safeStringify(exported, 5000);
      console.log('✅ Safe JSON.stringify completed successfully');
    } catch (stringifyError) {
      console.error('❌ Safe JSON.stringify failed:', stringifyError);
      
      // FALLBACK: Try again with annotations excluded
      console.log('🔄 Retrying with annotations excluded...');
      try {
        options.excludeAnnotations = true;
        exported = serialize(state, options);
        jsonData = await safeStringify(exported, 5000);
        console.log('✅ Fallback serialization successful');
        
        return {
          success: true,
          size: jsonData.length,
          excludedAnnotations: true,
        };
      } catch (fallbackError) {
        console.error('❌ Fallback serialization also failed:', fallbackError);
        throw new Error(`All serialization attempts failed: ${fallbackError instanceof Error ? fallbackError.message : 'Unknown error'}`);
      }
    }

    // Check size and apply storage limits
    const sizeBytes = new Blob([jsonData]).size;
    console.log(`📏 Draft size: ${formatDraftSize(sizeBytes)}`);

    if (sizeBytes > MAX_STORAGE_SIZE) {
      console.warn(`⚠️ Draft size (${formatDraftSize(sizeBytes)}) exceeds maximum allowed (${formatDraftSize(MAX_STORAGE_SIZE)})`);
      return {
        success: false,
        error: `Draft too large (${formatDraftSize(sizeBytes)}). Maximum allowed: ${formatDraftSize(MAX_STORAGE_SIZE)}`,
      };
    }

    // Try to save to localStorage
    try {
      const draftData = {
        timestamp: new Date().toISOString(),
        data: exported,
        size: sizeBytes,
      };
      
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
      console.log('✅ Draft saved to localStorage successfully');
      
      return {
        success: true,
        size: sizeBytes,
        excludedAnnotations: false,
      };
    } catch (storageError: any) {
      console.error('❌ localStorage save failed:', storageError);
      
      if (storageError.name === 'QuotaExceededError') {
        return {
          success: false,
          error: 'Storage quota exceeded. Please clear browser data or disable auto-save.',
        };
      }
      
      throw storageError;
    }

  } catch (error) {
    console.error('❌ saveDraftToStorage failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred during save',
    };
  }
}

/**
 * Load draft from localStorage and return serializable state
 */
export function loadDraftFromStorage(): LoadDraftResult {
  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY);
    
    if (!stored) {
      return { success: false, error: 'No draft found' };
    }

    let parsed: SceneExport;
    try {
      parsed = JSON.parse(stored);
    } catch (parseError) {
      return { success: false, error: 'Failed to parse stored draft - data may be corrupted' };
    }
    
    // Validate draft structure
    if (!parsed.data || !parsed.schemaVersion) {
      return { success: false, error: 'Invalid draft format' };
    }

    // Extract metadata
    const metadata: DraftMetadata = {
      timestamp: parsed.timestamp,
      size: stored.length,
      hasAnnotations: !!parsed.data.annotations,
      stateSlices: Object.keys(parsed.data),
    };

    // Hydrate the serialized data back to Redux state format
    const hydratedData = hydrate(parsed);

    return {
      success: true,
      data: hydratedData,
      metadata,
    };

  } catch (error) {
    console.warn('Failed to load draft from localStorage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to parse stored draft'
    };
  }
}

/**
 * Clear draft from localStorage
 */
export function clearDraftFromStorage(): boolean {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    return true;
  } catch (error) {
    console.warn('Failed to clear draft from localStorage:', error);
    return false;
  }
}

/**
 * Check if a draft is available in localStorage
 */
export function isDraftAvailable(): boolean {
  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY);
    return stored !== null && stored.length > 0;
  } catch (error) {
    return false;
  }
}

/**
 * Get draft metadata without fully loading the draft
 */
export function getDraftMetadata(): DraftMetadata | null {
  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY);
    
    if (!stored) {
      return null;
    }

    const parsed: SceneExport = JSON.parse(stored);
    
    return {
      timestamp: parsed.timestamp,
      size: stored.length,
      hasAnnotations: !!parsed.data.annotations,
      stateSlices: Object.keys(parsed.data),
    };

  } catch (error) {
    console.warn('Failed to get draft metadata:', error);
    return null;
  }
}

// =============================================================================
// DEBOUNCED SAVE UTILITIES
// =============================================================================

/**
 * Debounced save function to prevent excessive localStorage writes
 */
let saveTimeoutId: ReturnType<typeof setTimeout> | null = null;

export function debouncedSaveDraft(state: RootState, onComplete?: (result: SaveDraftResult) => void): void {
  console.log('⏰ debouncedSaveDraft called');
  
  // Clear existing timeout
  if (saveTimeoutId) {
    console.log('🔄 Clearing existing timeout');
    clearTimeout(saveTimeoutId);
  }

  // Set up new debounced save
  console.log('🔍 Setting up debounced save with 2-second delay...');
  saveTimeoutId = setTimeout(async () => {
    console.log('⏰ Debounce timeout fired, starting save...');
    try {
      console.log('🔍 About to call saveDraftToStorage...');
      const result = await saveDraftToStorage(state);
      console.log('🎉 saveDraftToStorage completed successfully');
      
      console.log('📋 Save result:', result);
      
      if (!result.success) {
        console.warn('❌ Auto-save failed:', result.error);
      } else {
        console.log('✅ Draft auto-saved successfully:', {
          size: result.size,
          excludedAnnotations: result.excludedAnnotations,
        });
      }
      
      // Call the completion callback if provided
      if (onComplete) {
        console.log('🔍 About to call onComplete callback...');
        try {
          onComplete(result);
          console.log('✅ onComplete callback completed successfully');
        } catch (callbackError) {
          console.error('❌ onComplete callback failed:', callbackError);
        }
      } else {
        console.log('ℹ️ No onComplete callback provided');
      }
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
      const errorResult: SaveDraftResult = {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
      if (onComplete) {
        console.log('🔍 Calling onComplete callback with error...');
        try {
          onComplete(errorResult);
          console.log('✅ onComplete callback with error completed');
        } catch (callbackError) {
          console.error('❌ onComplete callback failed:', callbackError);
        }
      }
    }
  }, SAVE_DEBOUNCE_MS); // Use the constant defined at the top
  
  console.log('🔍 Debounced save timeout set:', saveTimeoutId);
}

/**
 * Cancel any pending debounced save operation
 */
export function cancelDebouncedSave(): void {
  if (saveTimeoutId) {
    clearTimeout(saveTimeoutId);
    saveTimeoutId = null;
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format draft size for display
 */
export function formatDraftSize(bytes: number): string {
  if (bytes < 1000) {
    return `${bytes} bytes`;
  } else if (bytes < 1000000) {
    return `${Math.round(bytes / 100) / 10}KB`;
  } else {
    return `${Math.round(bytes / 100000) / 10}MB`;
  }
}

/**
 * Format draft timestamp for display
 */
export function formatDraftTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Unknown time';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins === 1) {
      return '1 minute ago';
    } else if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    } else if (diffMins < 1440) {
      const hours = Math.round(diffMins / 60);
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
  } catch (error) {
    return 'Unknown time';
  }
}

/**
 * Check if current state is significantly different from draft
 * Used to determine if auto-save is necessary
 */
export function hasSignificantChanges(currentState: RootState, draftData: any): boolean {
  try {
    // Quick comparison of key state indicators
    const current = serialize(currentState, { includeTimestamp: false });
    
    // Compare viewport (pan/zoom changes)
    if (JSON.stringify(current.data.viewport) !== JSON.stringify(draftData.viewport)) {
      return true;
    }

    // Compare expression count
    if (current.data.expressions?.objects?.length !== draftData.expressions?.objects?.length) {
      return true;
    }

    // Compare jump indicators count
    if (current.data.jumpIndicators?.jumps?.length !== draftData.jumpIndicators?.jumps?.length) {
      return true;
    }

    return false;
  } catch (error) {
    // If comparison fails, assume changes exist
    return true;
  }
}